import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import "core-js/modules/web.dom-collections.iterator.js";
import T from 'prop-types';
import { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { wrapEvents } from '../../helpers/wrapEvents';
import { useId } from '../../hooks/useId';
import { black, black16, black64, black8, purple, purple16, purple56, purpleAlpha56, red, redAlpha56, redAlpha8, redDarken8, white } from '../../styles/colors';
import { getFocusRing } from '../../styles/mixins';
import { rem } from '../../styles/rem';
import { flexbox, padding, system, variant } from '../../styles/system';
import { unit } from '../../styles/unit';
import { useFormField } from '../FormField/FormField';
import { Icon } from '../Icon/Icon';
import { Loader } from '../Loader/Loader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

function stateStyle({
  invalid,
  focused,
  highlighted,
  disabled
}) {
  if (disabled) {
    return css(["background:", ";border-color:", ";color:", ";"], black8, black8, black64);
  }

  if (invalid) {
    return css(["z-index:1;border:1px solid ", ";background-color:", ";color:", ";box-shadow:", ";"], red, redAlpha8, redDarken8, focused ? getFocusRing(redAlpha56) : 'none');
  }

  if (focused || highlighted) {
    return css(["z-index:1;border:1px solid ", ";box-shadow:", ";"], purple, getFocusRing(purpleAlpha56));
  }

  return css(["&:hover{border-color:", ";}"], purple56);
}

const stretchStyle = system({
  stretch: {
    property: 'width',
    transform: value => value ? '100%' : 'auto'
  }
});
const InputContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "InputContainer",
  componentId: "sc-16bv4pm-0"
})(["position:relative;display:inline-flex;align-self:flex-start;border:1px solid ", ";border-radius:4px;box-sizing:border-box;overflow:hidden;line-height:1.5em;background:", ";color:", ";", " ", " ", ";"], purple16, white, black, stretchStyle, stateStyle, variant({
  large: {
    fontSize: rem(20)
  },
  medium: {
    fontSize: rem(16)
  },
  small: {
    fontSize: rem(14)
  },
  xsmall: {
    fontSize: rem(14)
  }
}, 'size'));
const IconContainer = /*#__PURE__*/styled.label.withConfig({
  displayName: "IconContainer",
  componentId: "sc-16bv4pm-1"
})(["display:flex;align-items:center;justify-content:center;flex-shrink:0;box-sizing:content-box;width:", ";color:", ";", ""], unit(6), props => props.disabled ? black16 : black64, variant({
  large: {
    height: rem(46),
    paddingLeft: unit(3),
    '&& + *': {
      paddingLeft: unit(2)
    }
  },
  medium: {
    height: rem(38),
    paddingLeft: unit(2),
    '&& + *': {
      paddingLeft: unit(1)
    }
  },
  small: {
    height: rem(30),
    paddingLeft: unit(2),
    '&& + *': {
      paddingLeft: unit(1)
    }
  }
}, 'size'));
const StyledInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "StyledInput",
  componentId: "sc-16bv4pm-2"
})(["flex-grow:1;border:0;margin:0;appearance:none;font:inherit;letter-spacing:0.5px;box-shadow:none;background:none;outline:none;color:currentColor;overflow:hidden;", " &::placeholder{color:currentColor;opacity:0.48;}&[type='search']::-webkit-search-decoration,&[type='search']::-webkit-search-cancel-button,&[type='search']::-webkit-search-results-button,&[type='search']::-webkit-search-results-decoration{-webkit-appearance:none;}"], variant({
  large: {
    height: rem(46),
    padding: `10px ${unit(3)}`
  },
  medium: {
    height: rem(38),
    padding: `7px ${unit(3)}`
  },
  small: {
    height: rem(30),
    padding: `6px ${unit(2)}`
  },
  xsmall: {
    height: rem(24),
    padding: `0 ${unit(2)}`
  }
}, 'size'));
const TextInputAddon = /*#__PURE__*/styled.span.withConfig({
  displayName: "TextInputAddon",
  componentId: "sc-16bv4pm-3"
})(["display:flex;align-items:center;flex-shrink:0;", " ", ""], padding, flexbox);

function omitUndefinedValues(obj) {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];

    if (value !== undefined) {
      acc[key] = obj[key];
    }

    return acc;
  }, {});
}

function composeFormFieldProps(props, formFieldProps) {
  return Object.assign({}, formFieldProps, omitUndefinedValues(props));
}

const ICON_SIZE_MAP = {
  large: 'small',
  medium: 'xsmall',
  small: 'xxsmall',
  xsmall: 'xxsmall'
};
const TextInput = /*#__PURE__*/forwardRef(function TextInput(props, forwardedRef) {
  const formFieldProps = useFormField();

  const _composeFormFieldProp = composeFormFieldProps(props, formFieldProps),
        {
    rootRef,
    as,
    id,
    type = 'text',
    size = 'medium',
    disabled = false,
    highlighted = false,
    invalid = false,
    required = false,
    stretch = true,
    busy = false,
    glyph,
    addonStart,
    addonEnd,
    onFocus,
    onBlur
  } = _composeFormFieldProp,
        restProps = _objectWithoutPropertiesLoose(_composeFormFieldProp, ["rootRef", "as", "id", "type", "size", "disabled", "highlighted", "invalid", "required", "stretch", "busy", "glyph", "addonStart", "addonEnd", "onFocus", "onBlur"]);

  const generatedId = useId('input');
  const inputId = id || generatedId;
  const [focused, setFocused] = useState(false);
  return /*#__PURE__*/_jsxs(InputContainer, {
    ref: rootRef,
    size: size,
    disabled: disabled,
    focused: focused,
    highlighted: highlighted,
    invalid: invalid,
    stretch: stretch,
    children: [glyph && /*#__PURE__*/_jsx(IconContainer, {
      htmlFor: inputId,
      "aria-hidden": true,
      disabled: disabled,
      size: size,
      children: /*#__PURE__*/_jsx(Icon, {
        glyph: glyph,
        size: ICON_SIZE_MAP[size]
      })
    }), addonStart && addonStart, /*#__PURE__*/_jsx(StyledInput, Object.assign({}, restProps, {
      as: as,
      type: type,
      id: inputId,
      ref: forwardedRef,
      size: size,
      disabled: disabled,
      required: required,
      "aria-invalid": invalid || undefined,
      onFocus: wrapEvents(onFocus, () => {
        setFocused(true);
      }),
      onBlur: wrapEvents(onBlur, () => {
        setFocused(false);
      })
    })), busy && /*#__PURE__*/_jsx(TextInputAddon, {
      pr: 2,
      "aria-hidden": true,
      children: /*#__PURE__*/_jsx(Loader, {
        size: 2
      })
    }), addonEnd && addonEnd]
  });
});
TextInput.propTypes = {
  as: T.elementType,
  id: T.string,
  onChange: T.func,
  onFocus: T.func,
  onBlur: T.func,
  value: T.oneOfType([T.string, T.number]),
  type: T.oneOf(['text', 'password', 'email', 'number', 'tel', 'url', 'search', 'time']),
  disabled: T.bool,
  invalid: T.bool,
  required: T.bool,
  glyph: T.elementType,
  stretch: T.oneOfType([T.arrayOf(T.bool), T.bool]),
  size: T.oneOf(['large', 'medium', 'small', 'xsmall']),
  placeholder: T.string,
  addonStart: T.node,
  addonEnd: T.node,
  busy: T.bool,
  highlighted: T.bool
};
export { TextInput, TextInputAddon };